import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { useMutation, gql, DocumentNode, ApolloError } from '@apollo/client';
import { updateLiveUser } from '@firsttable/graphql-queries/src';
import withAnalytics from '@components/analytics/hocs/withAnalytics';
import { useSignupAttribution } from '@context/SignupAttribution';
import { resolveAuthMember, ssoEvent } from '@/lib/events';
import Logger from '@/lib/logger';
import { SUBURB_IDS_UNAVAILABLE_FIELD } from '@/components/organisms/Forms/Fields/SuburbIdsField';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';
import { AUTH_COMPLETION_ERROR_MESSAGE } from './constants';

const logger = new Logger('FormContext');
// logger.enable();

type mutationGQLType = { mutation: DocumentNode };
type apolloActionType = { submitAction: () => Promise<any> };
type FormActionType = mutationGQLType | apolloActionType;

type FormContextStateType = FormActionType & {
  extraInitialValues?: { [key: string]: any };
  complete?: (data: any, props: any) => void;
  handleError?: (data: any, props: any) => void;
  token?: string;
  cdnToken?: string;
  formFields?: any[]; // Want this to come from FormField but not typed yet
  transformInput?: (values: any, props: any) => any;
  transformResponse?: (values: any, props: any) => any;
  submitButtonText?: string | false;
  cancel?: () => void;
  mutation?: DocumentNode;
};

type updateType = (data: FormContextStateType, merge?: boolean) => void;
type resetType = (preserveState?: Partial<FormContextStateType>) => void;

export type FormContextType = {
  update: updateType;
  reset: resetType;
} & FormContextStateType;

const initialState: Partial<FormContextStateType> = {
  formFields: [],
  transformInput: (input, { formFields }) => {
    const values = { ...input };
    delete values[SUBURB_IDS_UNAVAILABLE_FIELD];
    formFields?.forEach((field) => {
      if (field.name === 'suburbIds') {
        if (Array.isArray(values[field.name])) {
          values[field.name] = values[field.name].map((id) => Number(id));
        }
        return;
      }

      if (values[field.name] ?? false) {
        switch (field?.dataType) {
          case 'ID':
          case 'INT':
            values[field.name] = parseInt(values[field.name], 10);
            break;
          case 'BOOLEAN':
            values[field.name] = ['true', '1', 1, true].includes(values[field.name]);
            break;
          case 'FLOAT':
            values[field.name] = parseFloat(values[field.name]);
            break;
          default:
            break;
        }
      }
    });
    return values;
  },
  // This function pops off the first GQL query from a mutation (normally the data you want)
  transformResponse: (data) => (data && data[Object.keys(data)[0]]) ?? {},
  complete: (
    auth,
    { handleLogin, token, cdnToken, member, redirectURL, update, method, values, track, identify, onComplete }
  ) => {
    logger.log(auth, member, token);

    if (auth?.action === 'FAILED') {
      throw new Error("Sorry, those details don't match our records");
    }

    const responseMember = resolveAuthMember(auth);

    if (!responseMember?.id) {
      reportErrorToNewRelic(new Error('Auth completion response did not contain a member ID'), {
        authMethod: method ?? 'unknown',
      });
      throw new Error(AUTH_COMPLETION_ERROR_MESSAGE);
    }

    const currentMember = resolveAuthMember(member) ?? {};
    const completedMember = { ...currentMember, ...responseMember };
    const completedAuth = { ...member, ...auth, member: completedMember };

    if (auth?.token && auth?.accountStatus === 'INCOMPLETE' && auth?.formFields?.length) {
      update({
        ...auth,
        mutation: updateLiveUser,
        formFields: auth.formFields,
        member: completedAuth,
      });
      logger.log('Member not complete, showing form');

      return;
    }
    logger.log('Member complete, auth user');

    // Track login or sign up events
    ssoEvent(method, completedAuth, logger, track, identify, { values });

    const result =
      (token || auth?.token) &&
      handleLogin({
        token: token || auth.token,
        cdnToken: cdnToken || auth.cdnToken,
        // need to remove Incomplete status that gets passed around
        member: completedMember,
        redirectURL,
      });
    if (result) onComplete?.(completedAuth);
    return result;
  },
};

export type FormContextProviderType = {
  state: Partial<FormContextStateType>;
  setFormState: (data: Partial<FormContextStateType>) => void;
  update: (data: Partial<FormContextStateType>, merge?: boolean) => void;
  reset: resetType;
};
const FormContext = createContext(null);

const { Provider } = FormContext;

const FormContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, setFormState] = useState(initialState);

  const reset = useCallback((preserveState: Partial<FormContextStateType> = {}) => {
    setFormState({ ...initialState, ...preserveState });
  }, []);

  const update = useCallback(
    (data: FormContextStateType, merge = true) => {
      setFormState((previousState) => {
        const lastState = merge ? previousState : initialState;
        const nextState = { cancel: reset, ...lastState, ...data };
        logger.log('Update Context', nextState);
        return nextState;
      });
    },
    [reset]
  );

  return <Provider value={{ state, setFormState, update, reset }}>{children}</Provider>;
};

const PLACEHOLDER_MUTATION = gql`
  mutation {
    NOT_SET_IN_FORM_CONTEXT
  }
`;

const withFormState = (ComponentToWrap) => {
  const WithState = (props) => {
    const { state, update } = useContext(FormContext);
    const signupAttribution = useSignupAttribution();
    const Component = ComponentToWrap;
    const formAction: {
      submitAction: () => Promise<any>;
      submitError: ApolloError;
      submitLoading: boolean;
    } = {
      submitAction: async () => undefined,
      submitError: null,
      submitLoading: false,
    };
    const options: {
      context: {
        headers?: { [index: string]: string };
      };
    } = {
      context: {},
    };

    if (state?.token) {
      const headers: { [index: string]: string } = {
        'Application-Authorization': `Bearer ${state.token}`,
      };
      if (state.cdnToken) {
        headers['cdn-token'] = `Bearer ${state.cdnToken}`;
      }
      options.context = { headers };
    }
    // if (state?.mutation) {
    const [action, { error, loading }] = useMutation(
      state?.mutation || PLACEHOLDER_MUTATION,
      options
    );
    // can we throw an error?
    formAction.submitAction = action;
    formAction.submitError = error;
    formAction.submitLoading = loading;

    logger.log(state);
    return (
      <Component
        {...state}
        update={update}
        formState={state}
        {...props}
        signupAttribution={signupAttribution}
        {...formAction}
      />
    );
  };
  WithState.displayName = 'withFormState';

  return WithState;
};

const withFormContext = (ComponentToWrap) => {
  const ComponentWithState = withFormState(ComponentToWrap);
  const ComponentWithAnalytics = withAnalytics(ComponentWithState);
  const WithContext = (props: any) => (
    <FormContextProvider>
      <ComponentWithAnalytics {...props} />
    </FormContextProvider>
  );
  WithContext.displayName = 'withFormContext';

  return WithContext;
};

export { FormContext, FormContextProvider, withFormContext, withFormState };
